import React from "react";
import { rowHasAllEvidenceLinks } from "./renderDpatEvidenceLink";
import { getDpatRelaxationYear, isDpatIncidentYear, isDpatRegistrationYear, isDpatScoreRelaxationExempt } from "./dpatScoreRelaxation";

/** Parses a count field; returns null for missing or non-numeric values (e.g. "N/A"). */
export function parseOptionalCount(value) {
  if (value == null || value === "" || value === "N/A") {
    return null;
  }

  const parsed = parseInt(String(value).replace(/,/g, ""), 10);
  return Number.isFinite(parsed) ? parsed : null;
}

function toFiniteNumber(value) {
  if (value == null || value === "" || value === "N/A") {
    return null;
  }

  if (typeof value === "number") {
    return Number.isFinite(value) ? value : null;
  }

  const parsed = parseFloat(String(value).replace(/,/g, ""));
  return Number.isFinite(parsed) ? parsed : null;
}

export function calculatePercentage(part, total) {
  const partNum = toFiniteNumber(part);
  const totalNum = toFiniteNumber(total);

  if (partNum == null || totalNum == null || totalNum === 0) {
    return 0;
  }

  return (partNum / totalNum) * 100;
}

export function countApplicationsForEachDomain(data, reports) {

  let noOfApplications = 0;
  let noOfApplicationsProcessed = 0;
  let noOfApplicationsProvided = 0;

  reports?.forEach(rep => {
    const currentReport = data.find(ser => ser.trackedEntity === rep.trackedEntity);

    if (currentReport) {
      const reps = rep.dataValues;
      reps.forEach(curRep => {
        if (curRep.dataElement === "vue6siD7aka") {
          noOfApplications += parseInt(curRep.value);
        }

        if (curRep.dataElement === "rn9j4w7pW9D") {
          noOfApplicationsProcessed += parseInt(curRep.value);
        }

        if (curRep.dataElement === "N0YnoMAm445") {
          noOfApplicationsProvided += parseInt(curRep.value);
        }
      })

    }
  });

  return [noOfApplications, noOfApplicationsProcessed, noOfApplicationsProvided];
}

export function getAttributeValue(key, val) {
  const attr = val?.attributes?.find(attr => attr?.displayName === key);
  return attr ? attr.value : "N/A";
};

function looksLikeDhis2Uid(value) {
  return /^[a-zA-Z0-9]{11}$/.test(String(value || "").trim());
}

function resolveTrackerDataValueLabel(dataValue) {
  const raw = String(dataValue?.value ?? "").trim();
  const label = String(dataValue?.displayName ?? "").trim();

  if (label && label !== raw && !looksLikeDhis2Uid(label)) {
    return label;
  }

  return raw || "N/A";
}

/** Resolves a tracker event data value by element id and/or display name labels. */
export function getTrackerEventDataValue(event, dataElementId, displayNames = []) {
  const dataValues = event?.dataValues || [];

  const byId = dataValues.find((dv) => dv.dataElement === dataElementId);
  if (byId?.value != null && byId.value !== "") {
    return resolveTrackerDataValueLabel(byId);
  }

  for (const name of displayNames) {
    const match = dataValues.find((dv) => dv.displayName === name);
    if (match?.value != null && match.value !== "") {
      return resolveTrackerDataValueLabel(match);
    }
  }

  return "N/A";
}

export function getYearFromDateValue(value) {
  if (value == null || value === "" || value === "N/A") {
    return null;
  }

  const date = new Date(value);
  if (!Number.isNaN(date.getTime())) {
    return date.getFullYear();
  }

  const match = String(value).match(/\b(19|20)\d{2}\b/);
  return match ? Number(match[0]) : null;
}

export function isDateInSelectedYear(dateValue, selectedYear) {
  const yearNum = Number(selectedYear);
  if (!Number.isFinite(yearNum)) {
    return false;
  }

  const dateYear = getYearFromDateValue(dateValue);
  if (dateYear == null) {
    return false;
  }

  if (isDpatScoreRelaxationExempt()) {
    return isDpatIncidentYear(dateYear, yearNum);
  }

  return dateYear === yearNum;
}

export function formatTrackerDate(value) {
  if (value == null || value === "" || value === "N/A") {
    return "N/A";
  }

  return String(value).split("T")[0];
}


export function formatDataGeneral(data, property, value) {
  return data?.filter(item =>
    item.attributes.some(attr =>
      attr.displayName === property && attr.value === value
    )
  );
}

export function getDataByTypes(data, property, values) {
  return data?.filter(item =>
    item.attributes.some(attr =>
      attr.displayName === property && values.includes(attr.value)
    )
  );
}

export function getProjectDetails(projects, reports) {
  const temp = [];
  projects.forEach((project, idx) => {

    const currentReport = reports.filter(rep => rep.trackedEntity === project.trackedEntity);
    let projectStatus = "";

    if (currentReport) {

      currentReport.forEach(curReport => {
        curReport.dataValues.forEach(rep => {
          if (rep.dataElement === "tE3QKB203nh") {
            projectStatus = rep.value;
          }

        });
      })

    }

    const dataSetTemp = {
      no: idx + 1,
      expectedStart: getAttributeValue("Expected Start Date", project),
      expectedCompletion: getAttributeValue("Expected Completion Date", project),
      department: getAttributeValue("Department", project),
      sector: getAttributeValue("Sector", project),
      rolloverCost: getAttributeValue("Rollover Cost", project),
      projectBuget: getAttributeValue("Actual Released", project),
      contract: getAttributeValue("Contract Sum", project),
      reviseContract: getAttributeValue("Revise Contract Sum", project),
      projectStatus
    };

    temp.push(dataSetTemp);
  });

  return temp;
}

export const groupProjectsBySectorAmountWithBudget = (projects, departments, sectorBudget, year) => {

  const grouped = {};
  const temp = [];

  departments.forEach(dep => {
    const departmentProjects = projects.filter(
      project => project.department && project.department.includes(dep)
    );

    let rolloverCost = 0;
    let newCost = 0;

    departmentProjects.forEach(p => {
      if (p.expectedStart.includes(year)) {
        newCost += p.projectBuget !== 'N/A' ? parseFloat(p.projectBuget) : 0;
      }

      if (!p.expectedStart.includes(year)) {
        const projectYear = new Date(p.expectedCompletion).getFullYear();
        if ((projectYear < year) && !p.projectStatus.includes("Completed")) {
          rolloverCost += p.rolloverCost !== 'N/A' ? parseFloat(p.rolloverCost) : 0;
        }
      }


    });

    grouped[dep] = departmentProjects;
    const currentSectorBudget = sectorBudget.find(s => s.sector === dep)
    const tempDataSet = {
      sector: dep,
      rolloverCost,
      newCost,
      capitalEnvelop: currentSectorBudget ? currentSectorBudget.budget : 0
    };

    temp.push(tempDataSet);

  });

  let newTotal = 0;
  let rolloverTotal = 0;
  let budgetTotal = 0

  temp.forEach(tp => {
    newTotal += tp.newCost;
    rolloverTotal += tp.rolloverCost;
    budgetTotal += parseFloat(tp.capitalEnvelop);
  });

  const total = {
    sector: "Total",
    rolloverCost: rolloverTotal,
    newCost: newTotal,
    capitalEnvelop: budgetTotal
  };

  temp.push(total);

  return temp;
}

export const groupProjectsBySectorAmountWithoutBudget = (projects, departments, year) => {

  const grouped = {};
  const temp = [];

  departments.forEach(dep => {
    const departmentProjects = projects.filter(
      project => project.department && project.department.includes(dep)
    );

    let rolloverCost = 0;
    let newCost = 0;
    let contractSum = 0;
    let reviseContractSum = 0;
    let actualPayment = 0

    departmentProjects.forEach(p => {
      if (p.expectedStart.includes(year)) {
        contractSum += p.contract !== 'N/A' ? parseFloat(p.contract) : 0;
        reviseContractSum += p.reviseContract !== 'N/A' ? parseFloat(p.reviseContract) : 0;
      }

      if (!p.expectedStart.includes(year)) {
        const projectYear = new Date(p.expectedCompletion).getFullYear();
        if ((projectYear < year) && !p.projectStatus.includes("Completed")) {
          rolloverCost += p.rolloverCost !== 'N/A' ? parseFloat(p.rolloverCost) : 0;
        }
      }


    });

    grouped[dep] = departmentProjects;
    const tempDataSet = {
      sector: dep,
      rolloverCost,
      contractSum,
      reviseContractSum,
      actualPayment,
      outstanding: parseFloat(contractSum) - parseFloat(actualPayment) - parseFloat(rolloverCost),
      percentage: 0
    };

    temp.push(tempDataSet);

  });

  let contractTotal = 0;
  let contractReviseTotal = 0;
  let actualTotal = 0;
  let outstandingTotal = 0;
  let rolloverTotal = 0;
  let percentage = 0;

  temp.forEach(tp => {
    contractTotal += parseFloat(tp.contractSum);
    rolloverTotal += parseFloat(tp.rolloverCost);
    contractReviseTotal += parseFloat(tp.reviseContractSum);
    actualTotal = parseFloat(tp.actualPayment);
    outstandingTotal = parseFloat(tp.outstanding);
    percentage = 0
  });

  const total = {
    sector: "Total",
    rolloverCost: rolloverTotal,
    contractSum: contractTotal,
    reviseContractSum: contractReviseTotal,
    actualPayment: actualTotal,
    outstanding: outstandingTotal,
    percentage
  };

  temp.push(total);

  return temp;
}

export const groupProjectByDevelopmentDimension = (projects, departments, year) => {

  const grouped = {};
  const temp = [];

  departments.forEach(dep => {
    const departmentProjects = projects.filter(
      project => project.department && project.department.includes(dep)
    );

    let rolloverCounter = 0;
    let newCounter = 0;

    departmentProjects.forEach(p => {
      if (p.expectedStart.includes(year)) {
        newCounter += 1;
      }

      if (!p.expectedStart.includes(year)) {
        const projectYear = new Date(p.expectedCompletion).getFullYear();
        if ((projectYear < year) && !p.projectStatus.includes("Completed")) {
          rolloverCounter += 1;
        }
      }

    });

    grouped[dep] = departmentProjects;
    const tempDataSet = {
      department: dep,
      rollover: rolloverCounter,
      new: newCounter

    };

    temp.push(tempDataSet);

  });

  let newTotal = 0;
  let rolloverTotal = 0;

  temp.forEach(tp => {
    newTotal += tp.new;
    rolloverTotal += tp.rollover
  });

  const total = {
    department: "Total",
    rollover: rolloverTotal,
    new: newTotal
  };

  temp.push(total);

  return temp;
}

export function getDataRank(index) {

  switch (index) {
    case 0: return "1st";
    case 1: return "2nd";
    case 2: return "3rd";
    case 3: return "4th";
    default: return "Other";
  }

}

export function filterTrackedEntitiesByCreatedAt(entities, year, period) {
  let start, end;

  switch (period) {
    case "Q1":
      start = `${year}-01-01`;
      end = `${year}-03-31`;
      break;
    case "Q2":
      start = `${year}-04-01`;
      end = `${year}-06-30`;
      break;
    case "Q3":
      start = `${year}-07-01`;
      end = `${year}-09-30`;
      break;
    case "Q4":
      start = `${year}-10-01`;
      end = `${year}-12-31`;
      break;
    case "yearly":
    default:
      start = `${year}-01-01`;
      end = `${year}-12-31`;
      break;
  }

  const startDate = new Date(start);
  const endDate = new Date(end);

  return entities.filter(entity => {
    const created = new Date(entity.createdAt);
    return created >= startDate && created <= endDate;
  });
}

export function filterTrackedEntitiesByYear(entities, startDate, endDate) {
  if (isDpatScoreRelaxationExempt()) {
    const selectedYear = getDpatRelaxationYear() ?? Number(String(endDate || startDate || "").slice(0, 4));
    return (entities || []).filter((entity) => {
      const createdYear = getYearFromDateValue(entity?.createdAt);
      return isDpatRegistrationYear(createdYear, selectedYear);
    });
  }

  return entities.filter(entity => {
    const created = new Date(entity.createdAt);
    return created >= startDate && created <= endDate;
  });
}


export function getPlanExecutionStats(formatedPlans, reports) {
  const counts = {};

  // Step 1: Count total and completed per `dd`
  formatedPlans.forEach(plan => {
    const dd = plan.dd;

    if (!counts[dd]) {
      counts[dd] = { total: 0, completed: 0 };
    }

    counts[dd].total += 1;

    const currentReport = reports.find(rep => rep.trackedEntity === plan.trackedEntity);

    if (currentReport) {
      const isCompleted = currentReport.dataValues.some(rep =>
        rep.dataElement === "SZcHb5mvjJx" && (rep.value === "Completed" || rep.value === "Ongoing" || rep.value === "Abandoned")
      );

      if (isCompleted) {
        counts[dd].completed += 1;
      }
    }
  });

  // Step 2: Convert to array with numbering
  const resultArray = Object.entries(counts).map(([dimension, values], index) => ({
    no: index + 1,
    dimension,
    planned: values.total,
    executed: values.completed
  }));

  // Step 3: Calculate total planned and executed
  const totalPlanned = resultArray.reduce((sum, item) => sum + item.planned, 0);
  const totalExecuted = resultArray.reduce((sum, item) => sum + item.executed, 0);

  // Step 4: Add the final summary row
  resultArray.push({
    dimension: "Total",
    planned: totalPlanned,
    executed: totalExecuted
  });

  return resultArray;
}


export function countByDd(data) {
  return data.reduce((acc, item) => {
    const key = item.dd;
    acc[key] = (acc[key] || 0) + 1;
    return acc;
  }, {});
}

export function getQuarterDate(index, year) {

  switch (index) {
    case 0: return `15/04/${year}`;
    case 1: return `15/07/${year}`;
    case 2: return `15/10/${year}`;
    case 3: return `15/01/${year + 1}`;
    default: return "Other";
  }

}

export function getFileLinkIfExist(reports, attribute, trackedEntity) {
  const currentReport = reports.find(rep => rep.trackedEntity === trackedEntity);

  if (currentReport) {
    const eventId = currentReport.event;
    for (const rep of currentReport.dataValues) {

      if (rep.dataElement === attribute) {
        return eventId;
      }
    }
  }

  return "";
}

export function getFirstFileLinkIfExist(reports, attribute, trackedEntity, stageId, type = "report") {
  const currentReport = reports.find(rep =>
    (rep.trackedEntity === trackedEntity) &&
    (rep.dataValues.length > 0) &&
    (rep.programStage === stageId)
  );

  if (type === "status") {
    if (currentReport) {

      for (const rep of currentReport.dataValues) {

        if (rep.dataElement === attribute) {
          return rep.value;
        }
      }
    }
  } else {
    if (currentReport) {
      const eventId = currentReport.event;
      for (const rep of currentReport.dataValues) {

        if (rep.dataElement === attribute) {
          return eventId;
        }
      }
    }
  }

  return "";
}

export function checkECANDGAMeetingFulfillment(meetings) {
  if (meetings.length === 0) {
    return 'Not Fulfilled';
  }

  const linkKeys = ['docs', 'attendance', 'recommendation'];

  for (const mt of meetings) {
    const ecDate = new Date(mt.ecaMeetingDate);
    const gaDate = new Date(mt.gaMeetingDate);

    if ((ecDate > gaDate) || !rowHasAllEvidenceLinks(mt, linkKeys)) {
      return 'Not Fulfilled';
    }
  }

  return 'Fulfilled';
}

export function normalizeSubStructureName(name) {
  return String(name || "")
    .trim()
    .toLowerCase()
    .replace(/\s+meeting$/i, "");
}

export function countUniqueSubStructureMeetings(subMeetings) {
  const names = new Set();
  (subMeetings || []).forEach((entry) => {
    const normalized = normalizeSubStructureName(entry?.meeting);
    if (normalized) names.add(normalized);
  });
  return names.size;
}

export function formatSubStructureMeetings(data) {
  const grouped = {};

  data.filter(val => val.meeting !== "N/A").forEach(item => {
    const meetingName = item.meeting;
    const groupKey = normalizeSubStructureName(meetingName) || meetingName;
    if (!grouped[groupKey]) {
      grouped[groupKey] = {
        meeting: meetingName,
        firstMeeting: null,
        secondMeeting: null,
        thirdMeeting: null,
        firstLink: null,
        secondLink: null,
        thirdLink: null
      };
    }

    // Find the next available slot (first, second, third)
    if (!grouped[groupKey].firstMeeting) {
      grouped[groupKey].firstMeeting = item.meetingDate;
      grouped[groupKey].firstLink = item.docs;
    } else if (!grouped[groupKey].secondMeeting) {
      grouped[groupKey].secondMeeting = item.meetingDate;
      grouped[groupKey].secondLink = item.docs;
    } else if (!grouped[groupKey].thirdMeeting) {
      grouped[groupKey].thirdMeeting = item.meetingDate;
      grouped[groupKey].thirdLink = item.docs;
    }
  });

  return Object.values(grouped);
}

export function formatSubStatutoryMeetings(data) {
  const grouped = {};

  data.filter(val => val.meeting !== "N/A").forEach(item => {
    const meetingName = item.meeting;
    if (!grouped[meetingName]) {
      grouped[meetingName] = {
        meeting: meetingName,
        firstMeeting: null,
        secondMeeting: null,
        thirdMeeting: null,
        firstLink: null,
        secondLink: null,
        thirdLink: null
      };
    }

    // Find the next available slot (first, second, third)
    if (!grouped[meetingName].firstMeeting) {
      grouped[meetingName].firstMeeting = item.meetingDate;
      grouped[meetingName].firstLink = item.docs;
    } else if (!grouped[meetingName].secondMeeting) {
      grouped[meetingName].secondMeeting = item.meetingDate;
      grouped[meetingName].secondLink = item.docs;
    } else if (!grouped[meetingName].thirdMeeting) {
      grouped[meetingName].thirdMeeting = item.meetingDate;
      grouped[meetingName].thirdLink = item.docs;
    }
  });

  return Object.values(grouped);
}

/** One table row with 1st/2nd/3rd meeting dates and minute links for a meeting series (e.g. GA, EC). */
export function buildGroupedMeetingSummaryRow(label, meetingList, reports, dataElementUid = "LAe1t59jYNT") {
  const rows = (meetingList || []).map((meeting) => {
    const minuteLink = getFileLinkIfExist(reports, dataElementUid, meeting.trackedEntity);
    return {
      meeting: label,
      meetingDate: getAttributeValue("DPAT | Meeting Date", meeting),
      docs: minuteLink ? (
        <a
          className="px-2 text-primary fw-bold text-decoration-underline"
          href={`https://dddp.gov.gh/api/events/files?eventUid=${minuteLink}&dataElementUid=${dataElementUid}`}
          target="_blank"
          rel="noopener noreferrer"
          title="Click here to see the uploaded minutes"
        >
          View Minutes
        </a>
      ) : (
        "Not Uploaded"
      ),
    };
  });

  const grouped = formatSubStructureMeetings(rows);
  if (grouped.length > 0) {
    return { key: label, ...grouped[0] };
  }

  return {
    key: label,
    meeting: label,
    firstMeeting: getAttributeValue("DPAT | Meeting Date", meetingList?.[0]),
    secondMeeting: getAttributeValue("DPAT | Meeting Date", meetingList?.[1]),
    thirdMeeting: getAttributeValue("DPAT | Meeting Date", meetingList?.[2]),
    firstLink: "Not Uploaded",
    secondLink: "Not Uploaded",
    thirdLink: "Not Uploaded",
  };
}

export const getMeetingRank = (index, type) => {
  if (type === 'GA') {
    switch (index) {
      case 0: return "1st Ordinary Meeting";
      case 1: return "2nd Ordinary Meeting";
      case 2: return "3rd Ordinary Meeting";
      default: return "Bonus Ordinary Meeting";
    }
  }
  else if (type === 'EC') {
    switch (index) {
      case 0: return "1st";
      case 1: return "2nd";
      case 2: return "3rd";
      default: return "Other";
    }
  } else if (type === 'Management Meetings') {
    switch (index) {
      case 0: return "1st";
      case 1: return "2nd";
      case 2: return "3rd";
      case 3: return "4th";
      default: return "Other";
    }
  } else if ((type === 'Entity Tender Committee (ETC)') || (type === 'Audit Committee')) {
    switch (index) {
      case 0: return "1st Quarter";
      case 1: return "2nd Quarter";
      case 2: return "3rd Quarter";
      case 3: return "4th Quarter";
      default: return "Other";
    }
  }
};


export const getStageValue = (currentReport, uuid) => {

  for (let rep of currentReport.dataValues) {
    if (rep.dataElement === uuid) {
      return parseFloat(rep.value);
    }
  }

}


// DPAT / assessment performance tiers (pass mark 33.5%)
export const DPAT_PASS_MARK_PERCENT = 33.5;

export const DPAT_PERFORMANCE_TIER_RANGES = [
  { classification: "Excellent", label: "Excellent (76–100%)", min: 76, max: 100 },
  { classification: "Very Good", label: "Very Good (61–75%)", min: 61, max: 75 },
  { classification: "Good", label: "Good (41–60%)", min: 41, max: 60 },
  { classification: "Satisfactory", label: "Satisfactory (33.5–40%)", min: 33.5, max: 40 },
  {
    classification: "Unsatisfactory",
    label: "Unsatisfactory (below 33.5%)",
    min: 0,
    max: 33.4,
  },
];

export function getPerformanceTierRangeLabel(classification) {
  const tier = DPAT_PERFORMANCE_TIER_RANGES.find((t) => t.classification === classification);
  return tier?.label ?? classification;
}

export function isAtOrAbovePassMark(percent) {
  const n = Number(percent);
  return Number.isFinite(n) && n >= DPAT_PASS_MARK_PERCENT;
}

function classifyPercentValue(percent) {
  const n = Number(percent);
  if (!Number.isFinite(n)) return "Unsatisfactory";
  if (n >= 76) return "Excellent";
  if (n >= 61) return "Very Good";
  if (n >= 41) return "Good";
  if (n >= DPAT_PASS_MARK_PERCENT) return "Satisfactory";
  return "Unsatisfactory";
}

/** Classify a value already on the 0–100 % scale (e.g. DPAT Overall Score %). */
export function classifyPerformancePercent(percent) {
  return classifyPercentValue(percent);
}

/**
 * Classify a raw score. Applies 0–4 → % conversion only for legacy 0–4 rubric inputs.
 * For values already in percent (0–100), use classifyPerformancePercent instead.
 */
export function classifyPerformance(rawScore) {
  return classifyPercentValue(normalizePercent(rawScore));
}

export function toOrdinal(value) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric) || numeric <= 0) return "-";
  const mod100 = numeric % 100;
  if (mod100 >= 11 && mod100 <= 13) return `${numeric}th`;
  const mod10 = numeric % 10;
  if (mod10 === 1) return `${numeric}st`;
  if (mod10 === 2) return `${numeric}nd`;
  if (mod10 === 3) return `${numeric}rd`;
  return `${numeric}th`;
}

function regionTotalScore(row) {
  if (row?.totalScore !== undefined && row?.totalScore !== null) return row.totalScore;
  return row?.regionAverageScore ?? 0;
}

function normalizePercent(rawScore) {
  const numeric = Number(rawScore);
  if (Number.isNaN(numeric)) return 0;
  // Legacy 0–4 rubric (4 = 100%). Do not use for DPAT % — use classifyPerformancePercent.
  if (numeric >= 0 && numeric <= 4 && Number.isInteger(numeric)) {
    return numeric * 25;
  }
  return numeric;
}

function performanceClass(performance) {
  if (performance === "Excellent") return "bg-emerald-500/20 text-emerald-700";
  if (performance === "Very Good") return "bg-sky-500/20 text-sky-700";
  if (performance === "Good") return "bg-yellow-400/30 text-yellow-800";
  if (performance === "Satisfactory") return "bg-orange-400/20 text-orange-700";
  return "bg-red-500/20 text-red-700";
}

export function getRegions() {
  return [
    {
      "displayName": "Ahafo Region",
      "id": "sQ7uY7OfGh9"
    },
    {
      "displayName": "Ashanti Region",
      "id": "dTJUQ6DTyI0"
    },
    {
      "displayName": "Bono East Region",
      "id": "L0G4TsktrO3"
    },
    {
      "displayName": "Bono Region",
      "id": "YwYDxOSrbRH"
    },
    {
      "displayName": "Central Region",
      "id": "c1LjZP7AHwc"
    },
    {
      "displayName": "Eastern Region",
      "id": "vNmQM26Brq4"
    },
    {
      "displayName": "Greater Accra Region",
      "id": "t7nEwtHs1Mv"
    },
    {
      "displayName": "North East Region",
      "id": "p7KW7Z2wpPh"
    },
    {
      "displayName": "Northern Region",
      "id": "uI6RzC2vT8n"
    },
    {
      "displayName": "Oti Region",
      "id": "CS9Dp4yJAMs"
    },
    {
      "displayName": "Savannah Region",
      "id": "uiZovYkacoA"
    },
    {
      "displayName": "Upper East Region",
      "id": "GIllXny74Ht"
    },
    {
      "displayName": "Upper West Region",
      "id": "GgfzSoi2pBE"
    },
    {
      "displayName": "Volta Region",
      "id": "OHNc6Xq1u0y"
    },
    {
      "displayName": "Western North Region",
      "id": "oIaZqOuUAnn"
    },
    {
      "displayName": "Western Region",
      "id": "RbzoqLb9jtt"
    }
  ];
}





